import { useExperienceBuilderContext } from "components/ExperienceBuilderEG/context/ExperienceBuilderContext";
import DOMPurify from "dompurify";
import { useCallback, useState } from "react";
import { Button, Checkbox, Loader } from "shared";
import { ButtonVariants } from "shared/Button/Button";
import { TOption } from "shared/Select/Select";
import TextInput from "shared/TextInput";
import IntlPhoneInput from "shared/IntlPhoneInput/IntlPhoneInput";
import * as R from "ramda";

export type TYourDetails = {
  golfingAssociation: string;
  title: TOption | null;
  firstName: string;
  lastName: string;
  email: string;
  countryCode: string;
  phoneNumber: string;
  isSubscriber: boolean;
};

const validateEmail = (email: string) =>
  String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );

function YourDetails() {
  const {
    labels,
    setActiveStep,
    yourDetails,
    onYourDetailsSubmit,
    selectedCourses,
    selectedHotels,
    selectedTours,
    isSubmitLoading,
    submitError,
  } = useExperienceBuilderContext();
  const [formValues, setFormValues] = useState<TYourDetails>(yourDetails);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [phoneValidationError, setPhoneValidationError] = useState("");

  const handlePhoneChange = useCallback(
    (fullNumber: string, _localNumber: string, dialCode: string) => {
      setFormValues((values) => ({
        ...values,
        phoneNumber: fullNumber,
        countryCode: dialCode,
      }));
    },
    [],
  );

  // IntlPhoneInput validates itself on input/blur/countrychange; surfacing its
  // result straight into formErrors makes the phone field react while typing
  const handlePhoneValidation = useCallback((error: string) => {
    setPhoneValidationError(error);
    setFormErrors((prev) =>
      error ? { ...prev, phoneNumber: error } : R.omit(["phoneNumber"], prev),
    );
  }, []);

  const getFieldError = (
    field: "firstName" | "lastName" | "email",
    value: string,
  ): string => {
    switch (field) {
      case "firstName":
        if (!value.length) return labels.YourDetails.FirstNameErrorMessage;
        if (value.length > 17)
          return labels.YourDetails.FirstNameMaxLengthErrorMessage;
        return "";
      case "lastName":
        if (!value.length) return labels.YourDetails.LastNameErrorMessage;
        if (value.length > 17)
          return labels.YourDetails.LastNameMaxLengthErrorMessage;
        return "";
      case "email":
        if (!value.length || !validateEmail(value))
          return labels.YourDetails.EmailErrorMessage;
        if (value.length > 100)
          return labels.YourDetails.EmailMaxLengthErrorMessage;
        return "";
    }
  };

  const handleFieldChange =
    (field: "firstName" | "lastName" | "email") => (value: string) => {
      setFormValues((values) => ({ ...values, [field]: value }));

      const error = getFieldError(field, value);
      setFormErrors((prev) =>
        error ? { ...prev, [field]: error } : R.omit([field], prev),
      );
    };

  const validateForm = (newFormValues?: TYourDetails) => {
    let errors: Record<string, string> = {};
    const formValuesToCheck = newFormValues ?? formValues;

    (["firstName", "lastName", "email"] as const).forEach((field) => {
      const error = getFieldError(field, formValuesToCheck[field]);
      if (error) {
        errors[field] = error;
      }
    });

    if (phoneValidationError) {
      errors.phoneNumber = phoneValidationError;
    } else if (!formValuesToCheck.phoneNumber) {
      errors.phoneNumber = labels.YourDetails.PhoneNumberErrorMessage;
    }

    setFormErrors(errors);

    return Object.keys(errors).length > 0;
  };

  const onSubmit = () => {
    const validationResult = validateForm();

    if (!validationResult) {
      onYourDetailsSubmit(formValues);
    }
  };

  return (
    <>
      <div className="u-wrapper m-your-details">
        {isSubmitLoading ? (
          <div
            style={{
              height: "600px",
              maxHeight: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gridColumn: "1 / -1",
            }}
          >
            <Loader />
          </div>
        ) : (
          <>
            <div className="m-your-details__left">
              <Button
                extraClass="back-btn"
                text="Back"
                variant={ButtonVariants.Naked}
                onClick={() => setActiveStep("transport")}
              />
              <h2 className="a-title__main">{labels.YourDetails.Title}</h2>

              <div className="m-image-with-text__pill-wrap">
                <div className="m-image-with-text__pill">
                  <span>
                    {selectedCourses.length}{" "}
                    {labels.YourDetails.SelectedCourses}
                  </span>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: labels.YourDetails.CourseIcon,
                    }}
                  ></span>
                </div>
                <div className="m-image-with-text__pill">
                  <span>
                    {selectedHotels.length} {labels.YourDetails.SelectedHotels}
                  </span>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: labels.YourDetails.HotelIcon,
                    }}
                  ></span>
                </div>
                <div className="m-image-with-text__pill">
                  <span>
                    {selectedTours.length} {labels.YourDetails.SelectedTours}
                  </span>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: labels.YourDetails.TourIcon,
                    }}
                  ></span>
                </div>
              </div>

              <div className="a-title__short-text">
                <p
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(labels.YourDetails.Description),
                  }}
                ></p>
              </div>
            </div>
            <div className="m-your-details__right">
              <div className="form-heading">{labels.YourDetails.FormTitle}</div>
              <div className="m-your-details__right-form">
                <TextInput
                  placeholder={labels.YourDetails.FirstNamePlaceholder}
                  value={formValues.firstName}
                  onChange={handleFieldChange("firstName")}
                  error={formErrors.firstName}
                />
                <TextInput
                  placeholder={labels.YourDetails.LastNamePlaceholder}
                  value={formValues.lastName}
                  onChange={handleFieldChange("lastName")}
                  error={formErrors.lastName}
                />
                <TextInput
                  placeholder={labels.YourDetails.EmailPlaceholder}
                  value={formValues.email}
                  onChange={handleFieldChange("email")}
                  type="email"
                  error={formErrors.email}
                />
                <IntlPhoneInput
                  placeholder={labels.YourDetails.PhoneNumberPlaceholder}
                  error={formErrors.phoneNumber}
                  defaultFullNumber={formValues.phoneNumber}
                  defaultCountryCode={formValues.countryCode}
                  errorLabels={{
                    required: labels.YourDetails.PhoneNumberErrorMessage,
                    invalidNumber: labels.YourDetails.PhoneNumberInvalidMessage,
                    tooShort: labels.YourDetails.PhoneNumberTooShortMessage,
                    tooLong: labels.YourDetails.PhoneNumberTooLongMessage,
                    invalidCountryCode:
                      labels.YourDetails.PhoneNumberInvalidCountryCodeMessage,
                  }}
                  onPhoneChange={handlePhoneChange}
                  onValidation={handlePhoneValidation}
                />
              </div>
              <div className="m-your-details__subscribe">
                <div>
                  <span>Subscribe to our newsletter</span>
                  <i className="zmdi zmdi-email"></i>
                </div>
                <Checkbox
                  id="subscribe"
                  name="subscribe"
                  checked={Boolean(formValues.isSubscriber)}
                  onChange={(value) => {
                    setFormValues((values) => ({
                      ...values,
                      isSubscriber: value,
                    }));
                  }}
                  label={labels.YourDetails.SubscribingText}
                />
              </div>

              {submitError && (
                <span className="submit-error">{submitError}</span>
              )}

              <Button
                text={labels.Common.SubmitBtn}
                onClick={onSubmit}
                extraClass="your-details-submit-btn"
                endIcon={<i className="zmdi zmdi-long-arrow-right"></i>}
              />
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(
                    labels.YourDetails.PrivacyPolicyText,
                  ),
                }}
              />
            </div>
          </>
        )}
      </div>
      <hr className="u-divider" />
    </>
  );
}

export default YourDetails;
